import { useUnit } from 'effector-react';
import { CircleSlash2 } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForbiddenUserAction } from 'entities/viewer';
import { User } from 'shared/api';
import {
	ConfirmModalProps,
	createConfirmModal,
	ModalFormActionIconProps,
	ModalFormButtonProps,
} from 'shared/ui';
import { BlockUserModel } from '../../model';

export const createBlockUserModal = (model: BlockUserModel) => {
	const ConfirmModal = createConfirmModal(model);

	const BlockUserModal = forwardRef<HTMLDivElement, ConfirmModalProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [user] = useUnit([model.$modalData]);

		return (
			<ConfirmModal
				ref={ref}
				title={t('block-user.title')}
				cancelButtonProps={{
					children: t('block-user.cancel'),
				}}
				submitButtonProps={{
					variant: 'outline',
					children: t('block-user.block'),
				}}
				{...props}
			>
				{t('block-user.description', { name: user?.name })}
			</ConfirmModal>
		);
	});

	const BlockUserModalButton = forwardRef<HTMLButtonElement, ModalFormButtonProps<User>>(
		({ modalData, ...props }, ref) => {
			const { t } = useTranslation();

			const disabled = useForbiddenUserAction(modalData);

			return (
				<ConfirmModal.Button
					ref={ref}
					data-test-id='block-user-button'
					variant='outline'
					leftSection={<CircleSlash2 />}
					disabled={disabled}
					modalData={modalData}
					{...props}
				>
					{t('block-user.block')}
				</ConfirmModal.Button>
			);
		},
	);

	const BlockUserModalActionIcon = forwardRef<HTMLButtonElement, ModalFormActionIconProps<User>>(
		({ modalData, ...props }, ref) => {
			const disabled = useForbiddenUserAction(modalData);

			return (
				<ConfirmModal.ActionIcon
					ref={ref}
					data-test-id='block-user-action-icon'
					variant='ghost-secondary'
					disabled={disabled}
					modalData={modalData}
					{...props}
				>
					<CircleSlash2 />
				</ConfirmModal.ActionIcon>
			);
		},
	);

	return Object.assign(BlockUserModal, {
		Button: BlockUserModalButton,
		ActionIcon: BlockUserModalActionIcon,
	});
};
